import { useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { GET_SIGNED_URL, GET_SIGNED_URL_MESH } from '../components/constants/constants';
import { useUserContext } from '../context/UserContext';

const useSignedUrlUpload = () => {
  const { userId } = useUserContext();
  const [uploading, setUploading] = useState(false);

  const getContentType = (fileName, uploadType, file) => {
    if (uploadType === 'mesh') {
      const extension = fileName?.split('.')?.pop()?.toLowerCase() || '';
      const typeMap = {
        'obj': 'model/obj',
        'stl': 'model/stl',
        'mesh': 'model/mesh'
      };
      return typeMap[extension] || 'application/octet-stream';
    }
    return file?.type || 'image/jpeg';
  };

  const uploadFile = async (file, uploadType = 'image', id) => {
    if (!file) {
      throw new Error('No file provided for upload');
    }

    let sanitizedFileName = file.name;
    if (uploadType === 'image') {
      sanitizedFileName = sanitizedFileName.replace(/\s+/g, '_');
    }

    const contentType = getContentType(sanitizedFileName, uploadType, file);

    const data = {
      type: uploadType === 'mesh' ? 'SaddleObj' : 'public',
      fileName: sanitizedFileName,
      fileType: contentType,
      ...(uploadType === 'mesh' && userId && { userId }),
      ...(uploadType === 'mesh' && id && { saddleId: id }),
    };

    const endpoint = uploadType === 'mesh' ? GET_SIGNED_URL_MESH : GET_SIGNED_URL;
    if (!endpoint) {
      const varName =
        uploadType === 'mesh' ? 'VITE_GET_SIGNED_URL_FOR_MESH' : 'VITE_GET_SIGNED_URL';
      throw new Error(
        `Missing ${varName} in .env — set it to your API Gateway POST /file/put-signed-url URL (see serverless info).`,
      );
    }

    try {
      setUploading(true);
      const { tokens } = await fetchAuthSession();
      const idToken = tokens?.idToken?.toString?.() || tokens?.idToken?.jwtToken;

      const apiKey =
        uploadType === 'mesh'
          ? import.meta.env.VITE_X_API_KEY_FOR_MESH
          : import.meta.env.VITE_X_API_KEY;

      const result = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(apiKey ? { 'x-api-key': apiKey } : {}),
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        },
        body: JSON.stringify(data),
      });

      if (!result.ok) {
        const errorResponse = await result.json().catch(() => ({}));
        throw new Error(
          errorResponse.message || 
          `Failed to get signed URL: ${result.statusText}`
        );
      }

      const response = await result.json();

      let putResponse;
      try {
        putResponse = await fetch(response.signedUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': contentType,
            ...(response.requiredHeaders || {}),
          },
          body: file,
        });
      } catch (err) {
        throw new Error(
          `S3 upload (browser to bucket) failed: ${err?.message || err}. If this persists, check the bucket CORS policy allows PUT from your origin.`,
        );
      }

      if (!putResponse.ok) {
        const errorText = await putResponse.text();
        console.error('S3 upload error details:', errorText);
        throw new Error(`Upload failed with status ${putResponse.status}`);
      }

      return response.fileUrl;
    } catch (error) {
      const isTypeError = error?.name === 'TypeError';
      const hint =
        isTypeError && String(error?.message || '').includes('fetch')
          ? ' (often CORS, blocked third-party requests, or network — in local dev ensure `yarn dev` proxy is active and try again)'
          : '';
      console.error('Upload error:', {
        error: error.message,
        stack: error.stack,
        fileInfo: { name: file?.name, size: file?.size }
      });
      throw new Error(`Upload failed: ${error.message}${hint}`);
    } finally {
      setUploading(false);
    }
  };

  return {
    uploading,
    uploadFile,
  };
};

export default useSignedUrlUpload;
